import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, InputAdornment, FormControlLabel, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import InvDataGrid from '../../components/InvDataGrid';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import PayloadJsonField, { parsePayloadJson, formatPayloadForField } from '../../components/master/PayloadJsonField';
import { InvTextField, InvSelect } from '../../components/InvField';
import MaterialMasterShell from '../../components/master/MaterialMasterShell';
import MasterListShell from '../../components/master/MasterListShell';
import FilterToolbar from '../../components/FilterToolbar';
import MasterFormDialog from '../../components/master/MasterFormDialog';
import ActiveStatusBadge from '../../components/master/ActiveStatusBadge';
import {
  getItemClasses, createItemClass, updateItemClass, deleteItemClass,
} from '../../api/itemClass';
import { getMaterialGroups } from '../../api/materialGroup';
import { MASTER_PAGE_SIZE, STATUS_FILTER_OPTIONS } from '../../constants';
import {
  normalizeItemClass, normalizeItemClassList, itemClassRowId, toInventoryPage,
  hierarchicalParentSelectOptions, resolveParentLabel, collectDescendantIds,
  normalizeMaterialGroupList, materialGroupRowId, toSelectOptions,
} from '../../utils/masterEntity';
import { formatDate } from '../../utils';
import { IPD_COLORS, cardSx, inputSx, switchSx } from '../../theme';
import useMasterList from '../../hooks/useMasterList';

const CLASS_KEYS = {
  idKey: 'item_class_id',
  nameKey: 'class_name',
  codeKey: 'class_code',
  parentKey: 'parent_class_id',
};

const EMPTY_FORM = {
  class_code: '',
  class_name: '',
  material_group_id: '',
  parent_class_id: '',
};

const EDIT_FORM = {
  class_name: '',
  parent_class_id: '',
  description: '',
  class_payload_json: '',
  is_active: true,
};

function validatePayloadJson(v) {
  try {
    parsePayloadJson(v);
    return true;
  } catch (e) {
    return e.message || 'Invalid JSON in advanced settings';
  }
}

function buildListParams({ page, search, statusFilter, pageSize }) {
  const params = { limit: pageSize, offset: page * pageSize };
  if (search.trim()) params.search = search.trim();
  if (statusFilter === 'active') params.is_active = true;
  if (statusFilter === 'inactive') params.is_active = false;
  return params;
}

function buildCreateBody(data) {
  return {
    class_code: data.class_code.trim().toUpperCase(),
    class_name: data.class_name.trim(),
    material_group_id: data.material_group_id,
    parent_class_id: data.parent_class_id || null,
    applicable_store_type: 'CENTRAL_STORE',
    description: '',
    class_payload: {},
    is_active: true,
  };
}

function buildUpdateBody(data) {
  return {
    class_name: data.class_name.trim(),
    parent_class_id: data.parent_class_id || null,
    description: data.description?.trim() || '',
    class_payload: parsePayloadJson(data.class_payload_json),
    is_active: data.is_active !== false,
  };
}

function mapRowToEditForm(row) {
  const parentId = row?.parent_class_id ?? row?.parent_class?.id ?? '';
  return {
    class_name: row?.class_name ?? '',
    parent_class_id: parentId === '' || parentId == null ? '' : String(parentId),
    description: row?.description ?? '',
    class_payload_json: formatPayloadForField(row?.class_payload),
    is_active: row?.is_active !== false,
  };
}

export default function ItemClassList() {
  const list = useMasterList({ pageSize: MASTER_PAGE_SIZE });
  const {
    items, setItems, totalCount, setTotalCount,
    loading, setLoading, error, setError,
    page, setPage, searchInput, setSearchInput,
    search, statusFilter, setStatusFilter,
    resetFilters, pageSize,
  } = list;

  const [allItemClasses, setAllItemClasses] = useState([]);
  const [materialGroups, setMaterialGroups] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues: EMPTY_FORM });
  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    formState: { errors: editErrors },
  } = useForm({ defaultValues: EDIT_FORM });

  const hasActiveFilters = Boolean(search.trim() || statusFilter);

  const loadAllItemClasses = useCallback(async () => {
    try {
      const data = await getItemClasses({ limit: 200, is_active: true });
      setAllItemClasses(normalizeItemClassList(data));
    } catch {
      setAllItemClasses([]);
    }
  }, []);

  const loadMaterialGroups = useCallback(async () => {
    try {
      const data = await getMaterialGroups({ limit: 200, is_active: true });
      setMaterialGroups(normalizeMaterialGroupList(data));
    } catch {
      setMaterialGroups([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await getItemClasses(buildListParams({ page, search, statusFilter, pageSize }));
      const { items: fetched, total } = toInventoryPage(raw, { page, pageSize });
      setItems(fetched.map(normalizeItemClass));
      setTotalCount(total);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, pageSize, setLoading, setError, setItems, setTotalCount]);

  useEffect(() => { loadAllItemClasses(); }, [loadAllItemClasses]);
  useEffect(() => { loadMaterialGroups(); }, [loadMaterialGroups]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (editTarget) resetEdit(mapRowToEditForm(editTarget));
  }, [editTarget, resetEdit]);

  const parentOpts = useMemo(
    () => hierarchicalParentSelectOptions(allItemClasses, CLASS_KEYS),
    [allItemClasses],
  );

  const materialGroupOptions = useMemo(() => toSelectOptions(materialGroups, {
    idKey: 'material_group_id', nameKey: 'group_name', codeKey: 'group_code',
  }), [materialGroups]);

  const resolveMaterialGroupName = useCallback((row) => {
    if (!row) return '—';
    if (row.material_group_name) return row.material_group_name;
    const group = materialGroups.find(
      g => String(materialGroupRowId(g)) === String(row.material_group_id),
    );
    return group?.group_name ?? '—';
  }, [materialGroups]);

  const editId = editTarget ? itemClassRowId(editTarget) : null;
  const editParentOpts = useMemo(() => {
    if (!editId) return parentOpts;
    const excludeIds = collectDescendantIds(allItemClasses, editId, CLASS_KEYS);
    return hierarchicalParentSelectOptions(allItemClasses, { ...CLASS_KEYS, excludeIds: [...excludeIds] });
  }, [allItemClasses, editId, parentOpts]);

  function openAddModal() {
    reset(EMPTY_FORM);
    setAddOpen(true);
  }

  function closeAddModal() {
    if (saving) return;
    setAddOpen(false);
    reset(EMPTY_FORM);
  }

  function closeEditModal() {
    setEditTarget(null);
    resetEdit(EDIT_FORM);
  }

  async function onSubmit(data) {
    setSaving(true);
    try {
      await createItemClass(buildCreateBody(data));
      closeAddModal();
      toast.success('Item class created');
      loadAllItemClasses();
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to create item class');
    } finally {
      setSaving(false);
    }
  }

  async function onEditSubmit(data) {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await updateItemClass(itemClassRowId(editTarget), buildUpdateBody(data));
      closeEditModal();
      toast.success('Item class updated');
      loadAllItemClasses();
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to update item class');
    } finally {
      setEditSaving(false);
    }
  }

  async function confirmDeactivate() {
    setDeactivating(true);
    try {
      await deleteItemClass(itemClassRowId(deactivateTarget));
      setDeactivateTarget(null);
      toast.success('Item class deactivated');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to deactivate item class');
      setDeactivateTarget(null);
    } finally {
      setDeactivating(false);
    }
  }

  const columns = useMemo(() => [
    { field: 'class_code', headerName: 'Code', width: 110 },
    { field: 'class_name', headerName: 'Name', flex: 1, minWidth: 160 },
    {
      field: 'material_group_id',
      headerName: 'Material Group',
      width: 160,
      valueGetter: (p) => resolveMaterialGroupName(p.row),
    },
    {
      field: 'parent_class_id',
      headerName: 'Parent',
      width: 150,
      valueGetter: (p) => resolveParentLabel(p.row, allItemClasses, CLASS_KEYS),
    },
    { field: 'applicable_store_type', headerName: 'Store Type', width: 130 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (p) => <ActiveStatusBadge active={p.value} />,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 110,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit item class">
            <IconButton size="small" onClick={() => setEditTarget(p.row)} sx={{ color: IPD_COLORS.primary }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.is_active !== false && (
            <Tooltip title="Deactivate item class">
              <IconButton size="small" onClick={() => setDeactivateTarget(p.row)} sx={{ color: IPD_COLORS.error.text }}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [allItemClasses, resolveMaterialGroupName]);

  const listSubtitle = hasActiveFilters
    ? 'Showing filtered results — adjust or reset filters to see all item classes.'
    : 'Define item classes to group similar inventory items by store type and behavior.';

  return (
    <MaterialMasterShell
      title="Item Class Master"
      description="Configure item classes that control how inventory items are grouped and processed."
      badge={`${totalCount} ${totalCount === 1 ? 'class' : 'classes'}`}
      actions={
        <InvButton startIcon={<AddIcon />} onClick={openAddModal}>
          Add Item Class
        </InvButton>
      }
    >
      <MasterListShell
        title="All Item Classes"
        subtitle={listSubtitle}
        filters={(
          <>
            <FilterToolbar.Search
              placeholder="Search by name or code..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            <FilterToolbar.Filter
              label="Status"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              options={STATUS_FILTER_OPTIONS}
            />
            <FilterToolbar.Reset onClick={resetFilters} disabled={!hasActiveFilters} />
          </>
        )}
      >
        <InvDataGrid
          embedded
          fillHeight
          rows={items}
          columns={columns}
          loading={loading}
          error={error}
          onRetry={load}
          getRowId={itemClassRowId}
          emptyMessage="No item classes yet"
          emptyDescription="Create your first item class to define how inventory items are categorized. Advanced settings can be added when editing."
          emptyAction={<InvButton startIcon={<AddIcon />} onClick={openAddModal}>Add Item Class</InvButton>}
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => setPage(model.page)}
          pageSizeOptions={[pageSize]}
        />
      </MasterListShell>

      <MasterFormDialog
        open={addOpen}
        title="Add Item Class"
        subtitle="Enter a unique name and code. Store type and advanced settings can be configured after creation."
        formId="add-item-class-form"
        onSubmit={handleSubmit(onSubmit)}
        onClose={closeAddModal}
        saving={saving}
        submitLabel="Add Item Class"
        submitIcon={<AddIcon />}
      >
        <Controller
          name="class_name"
          control={control}
          rules={{ required: 'Class name is required' }}
          render={({ field }) => (
            <InvTextField {...field} label="Class Name *" error={errors.class_name} autoFocus />
          )}
        />
        <Controller
          name="class_code"
          control={control}
          rules={{ required: 'Class code is required' }}
          render={({ field }) => (
            <InvTextField
              {...field}
              label="Class Code *"
              error={errors.class_code}
              helperText="Short unique identifier (auto-uppercased)"
              onChange={e => field.onChange(e.target.value.toUpperCase())}
            />
          )}
        />
        <Controller
          name="material_group_id"
          control={control}
          rules={{ required: 'Material group is required' }}
          render={({ field }) => (
            <InvSelect
              label="Material Group *"
              value={field.value}
              onChange={field.onChange}
              options={materialGroupOptions}
              error={errors.material_group_id}
            />
          )}
        />
        <Controller
          name="parent_class_id"
          control={control}
          render={({ field }) => (
            <InvSelect
              label="Parent Class"
              value={field.value}
              onChange={field.onChange}
              options={parentOpts}
              helperText="Leave empty for a top-level class."
            />
          )}
        />
      </MasterFormDialog>

      <Dialog
        open={Boolean(editTarget)}
        onClose={editSaving ? undefined : closeEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: cardSx }}
      >
        <DialogTitle sx={{ color: IPD_COLORS.textPrimary, display: 'flex', justifyContent: 'space-between', pr: 1 }}>
          Edit Item Class
          <IconButton onClick={closeEditModal} disabled={editSaving} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="form"
            id="edit-item-class-form"
            onSubmit={handleEditSubmit(onEditSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}
          >
            <InvTextField label="Class Code" value={editTarget?.class_code || ''} disabled />
            <InvTextField label="Material Group" value={resolveMaterialGroupName(editTarget)} disabled />
            <InvTextField label="Store Type" value={editTarget?.applicable_store_type || ''} disabled />
            <Controller
              name="class_name"
              control={editControl}
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <InvTextField {...field} label="Class Name *" error={editErrors.class_name} />
              )}
            />
            <Controller
              name="parent_class_id"
              control={editControl}
              render={({ field }) => (
                <InvSelect
                  label="Parent Class"
                  value={field.value}
                  onChange={field.onChange}
                  options={editParentOpts}
                  helperText="Leave empty for a top-level class."
                />
              )}
            />
            <Controller
              name="description"
              control={editControl}
              render={({ field }) => (
                <InvTextField {...field} label="Description" multiline rows={2} />
              )}
            />
            <Controller
              name="class_payload_json"
              control={editControl}
              rules={{ validate: validatePayloadJson }}
              render={({ field }) => (
                <PayloadJsonField
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  error={editErrors.class_payload_json}
                />
              )}
            />
            <Controller
              name="is_active"
              control={editControl}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} sx={switchSx} />
                  }
                  label="Active"
                  sx={{ color: IPD_COLORS.textPrimary, m: 0 }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <InvButton variant="outlined" tone="teal" onClick={closeEditModal} disabled={editSaving}>Cancel</InvButton>
          <InvButton type="submit" form="edit-item-class-form" loading={editSaving}>Save Changes</InvButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        title="Deactivate Item Class"
        description={`Deactivate "${deactivateTarget?.class_name}"? It will no longer be available for new items.`}
        destructive
        loading={deactivating}
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateTarget(null)}
      />
    </MaterialMasterShell>
  );
}
